import React, { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Check, GitBranch, Cpu, Flag, ShieldCheck, RotateCw,
  CheckCircle2, XCircle, FileBarChart, Eye, EyeOff,
  AlertOctagon, RefreshCcw,
} from "lucide-react";

/**
 * v1.2 PR-B.1 — Super-admin build & version page.
 *
 * Sources:
 *   GET /api/__version                               -> git sha + code fingerprint
 *   GET /api/super-admin/system/env-redacted         -> redacted env snapshot
 *   GET /api/super-admin/system/feature-flags        -> DB + env-driven flags
 *   GET /api/super-admin/system/lint-status          -> cached lint guard results
 */
export default function SuperAdminBuild() {
  const [info, setInfo] = useState(null);
  const [env, setEnv] = useState(null);
  const [flags, setFlags] = useState(null);
  const [lint, setLint] = useState(null);
  const [lintLoading, setLintLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRedactedSecrets, setShowRedactedSecrets] = useState(false);

  const loadAll = useCallback(async () => {
    api.get("/__version").then(({ data }) => setInfo(data)).catch(() => {});
    api.get("/super-admin/system/env-redacted").then(({ data }) => setEnv(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/super-admin/system/feature-flags").then(({ data }) => setFlags(data)).catch(() => {});
    api.get("/super-admin/system/lint-status").then(({ data }) => setLint(data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshLint = async () => {
    setLintLoading(true);
    try {
      const { data } = await api.get("/super-admin/system/lint-status", { params: { refresh: true } });
      setLint(data);
      toast.success(data.all_ok ? "Lint guards pass" : "Lint guards report failures");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLintLoading(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(info, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <SuperAdminLayout>
      <div data-testid="super-admin-build" className="space-y-6">
        <header>
          <div className="eb-overline mb-1">Super admin</div>
          <h1 className="text-3xl font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
            <Cpu className="w-7 h-7 text-orange-500" /> Build &amp; version
          </h1>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-3xl">
            Confirms exactly which code is running. Per-file fingerprints catch silent stale-deploy drift.
          </p>
        </header>

        {/* v1.2 PR-B.4 — runtime drift banner */}
        {info && info.runtime_matches_disk === false && (
          <RuntimeDriftBanner drift={info.runtime_drift} onBounced={loadAll} />
        )}

        {/* Version JSON */}
        <section className="bg-white border border-navy-100 rounded-xl overflow-hidden" data-testid="build-version-card">
          <div className="flex items-center justify-between px-5 py-3 border-b border-navy-100">
            <div className="inline-flex items-center gap-2 text-xs font-mono text-navy-500">
              <GitBranch className="w-3.5 h-3.5" />
              <strong className="text-navy-900">{info?.git_sha || "—"}</strong>
              <span className="mx-1">·</span>
              {info?.build_timestamp || "—"}
            </div>
            <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs text-navy-600 hover:text-navy-900" data-testid="sa-build-copy">
              {copied ? <><Check className="w-3.5 h-3.5 text-success" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy JSON</>}
            </button>
          </div>
          {!info ? (
            <div className="px-5 py-4 text-sm text-navy-400">Loading…</div>
          ) : (
            <div className="px-5 py-4 grid gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-navy-600">Code fingerprint</h3>
              <table className="w-full text-xs font-mono">
                <tbody className="divide-y divide-navy-100">
                  {Object.entries(info.code_fingerprint || {}).map(([file, hash]) => (
                    <tr key={file}>
                      <td className="py-1.5 pr-3 text-navy-700 break-all">{file}</td>
                      <td className="py-1.5 text-right">
                        {hash === "missing" ? (
                          <span className="text-danger">missing</span>
                        ) : (
                          <span className="text-navy-500">{hash}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Lint guards */}
        <section className="bg-white border border-navy-100 rounded-xl p-5" data-testid="build-lint-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-navy-900 inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-orange-500" /> Lint guards
              </h3>
              <p className="text-xs text-navy-500 mt-1">
                Frontend mock-leak, nav discoverability, and backend mock-leak guards.
                Cached for 1 hour — click Re-run for a fresh check.
              </p>
            </div>
            <button onClick={refreshLint} disabled={lintLoading}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-navy-200 hover:bg-navy-50 disabled:opacity-50"
                    data-testid="lint-refresh-btn">
              <RotateCw className={`w-3.5 h-3.5 ${lintLoading ? "animate-spin" : ""}`} />
              {lintLoading ? "Running…" : "Re-run"}
            </button>
          </div>
          {!lint ? (
            <div className="text-sm text-navy-400">Loading…</div>
          ) : (
            <div>
              <div className={`mb-3 inline-flex items-center gap-2 px-2.5 py-1 rounded text-xs font-medium ${
                lint.all_ok ? "bg-success/10 text-success" : "bg-danger/10 text-danger"
              }`}>
                {lint.all_ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                {lint.all_ok ? "All guards pass" : "Failures present"}
                {lint.from_cache && (
                  <span className="opacity-60">· cached {lint.cache_age_seconds}s ago</span>
                )}
              </div>
              <ul className="space-y-2" data-testid="lint-results">
                {lint.checks?.map((c) => (
                  <li key={c.name} className="text-xs font-mono p-2 rounded border border-navy-100 bg-navy-50/30">
                    <div className="inline-flex items-center gap-1.5 mb-1">
                      {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-success" /> : <XCircle className="w-3.5 h-3.5 text-danger" />}
                      <span className="font-semibold text-navy-900">{c.name}</span>
                      <span className="text-navy-500">· exit {c.exit_code}</span>
                    </div>
                    {!c.ok && (c.stderr || c.stdout) && (
                      <pre className="text-[10px] text-navy-700 overflow-x-auto whitespace-pre-wrap mt-1">
                        {(c.stderr || c.stdout || "").slice(0, 1200)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Feature flags */}
        <section className="bg-white border border-navy-100 rounded-xl p-5" data-testid="build-flags-card">
          <h3 className="font-semibold text-navy-900 inline-flex items-center gap-2 mb-3">
            <Flag className="w-4 h-4 text-orange-500" /> Feature flags
          </h3>
          {!flags ? (
            <div className="text-sm text-navy-400">Loading…</div>
          ) : flags.flags.length === 0 ? (
            <div className="text-sm text-navy-500">No feature flags configured.</div>
          ) : (
            <ul className="divide-y divide-navy-100">
              {flags.flags.map((f) => (
                <li key={f.key} className="py-2 flex items-center justify-between text-sm">
                  <div className="flex flex-col">
                    <span className="font-mono text-navy-800">{f.key}</span>
                    {f.description && <span className="text-xs text-navy-500">{f.description}</span>}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                    f.enabled
                      ? "bg-success/10 text-success border-success/30"
                      : "bg-navy-50 text-navy-500 border-navy-200"
                  }`}>
                    {f.enabled ? "ON" : "OFF"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Env dump */}
        <section className="bg-white border border-navy-100 rounded-xl p-5" data-testid="build-env-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-navy-900 inline-flex items-center gap-2">
                <FileBarChart className="w-4 h-4 text-orange-500" /> Environment (redacted)
              </h3>
              <p className="text-xs text-navy-500 mt-1">
                Secrets are redacted to <code className="px-1 bg-navy-50 rounded">***last4</code>.
                Non-secret env vars in clear.
              </p>
            </div>
            <button
              onClick={() => setShowRedactedSecrets((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-navy-200 hover:bg-navy-50"
              data-testid="env-toggle-secrets"
            >
              {showRedactedSecrets ? <><EyeOff className="w-3.5 h-3.5" /> Hide secrets</> : <><Eye className="w-3.5 h-3.5" /> Show secret rows</>}
            </button>
          </div>
          {!env ? (
            <div className="text-sm text-navy-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs font-mono">
                <tbody className="divide-y divide-navy-100">
                  {env.env
                    .filter((e) => showRedactedSecrets || !e.is_secret)
                    .map((row) => (
                      <tr key={row.name} className={row.is_secret ? "bg-warning/5" : ""}>
                        <td className="py-1 pr-3 text-navy-800 align-top break-all">{row.name}</td>
                        <td className="py-1 text-navy-600 break-all">
                          {row.is_secret && <span className="inline-block text-warning text-[10px] uppercase tracking-wider mr-1.5">secret</span>}
                          {row.value}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </SuperAdminLayout>
  );
}

// ---- v1.2 PR-B.4 — Runtime-drift banner & manual bounce ----
function RuntimeDriftBanner({ drift, onBounced }) {
  const [busy, setBusy] = useState(false);
  const bounce = async () => {
    if (!window.confirm(
      "Restart the backend worker?\n\n"
      + "The API will be unavailable for ~2 seconds while supervisor re-spawns the worker. "
      + "Use this after a deploy when this banner says new router files are on disk but "
      + "the running process is stale."
    )) return;
    setBusy(true);
    try {
      await api.post("/__deploy/restart");
      toast.success("Worker SIGTERM scheduled. Refreshing version info in 3s…");
      // Give supervisor 3s to respawn, then re-fetch /__version.
      setTimeout(() => { onBounced(); setBusy(false); }, 3500);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };
  return (
    <section
      className="bg-danger/10 border border-danger/30 rounded-xl p-4 space-y-3"
      data-testid="runtime-drift-banner"
    >
      <div className="flex items-start gap-3">
        <AlertOctagon className="w-6 h-6 text-danger flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-danger">Running process is stale — restart required</h3>
          <p className="text-sm text-navy-800 dark:text-navy-100 mt-1">
            {drift?.missing_in_runtime?.length === 1
              ? "1 router file"
              : `${drift?.missing_in_runtime?.length || 0} router files`}{" "}
            on disk are not imported by the running worker. The most likely cause is a redeploy
            that copied new source code but didn't bounce the worker. Click <strong>Restart
            worker</strong> below, or contact Emergent Support if the restart endpoint is
            unavailable.
          </p>
          {drift?.missing_in_runtime?.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-danger font-medium">
                Show missing modules ({drift.missing_in_runtime.length})
              </summary>
              <ul className="mt-1 font-mono pl-4 list-disc text-navy-700 dark:text-navy-200">
                {drift.missing_in_runtime.map((m) => <li key={m}>routers/{m}.py</li>)}
              </ul>
            </details>
          )}
        </div>
        <button
          onClick={bounce}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-danger text-white hover:bg-danger/90 disabled:opacity-50"
          data-testid="runtime-drift-restart-btn"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Restarting…" : "Restart worker"}
        </button>
      </div>
    </section>
  );
}
